import type { IntegrationType } from "@v2/features/settings/shop/integrations/IntegrationForm";

/**
 * Categories group integrations by the job they help a commerce business accomplish.
 * Each integration belongs to exactly one category.
 */
type IntegrationCategory = {
  id: string;
  label: string;
  description: string;
  /** Extra search terms that match all integrations in this category. */
  keywords: string[];
  integrations: IntegrationType[];
};

/**
 * Recommended integrations highlighted in the carousel at the top of the page.
 * Each entry has a headline, subtitle, and gradient for the full-width graphic.
 * Optionally, an image can be provided instead of using the gradient.
 */
export type RecommendedIntegration = {
  integrationType: IntegrationType;
  headline: string;
  subtitle: string;
  gradient: string;
  image?: string; // Optional graphic image path
};

export const RECOMMENDED_INTEGRATIONS: RecommendedIntegration[] = [
  {
    integrationType: "Klaviyo",
    headline: "Supercharge your email & SMS",
    subtitle:
      "Connect Klaviyo to capture subscribers and trigger personalized flows directly from your pages.",
    gradient: "from-emerald-400/30 via-teal-300/20 to-cyan-200/30",
  },
  {
    integrationType: "GA4",
    headline: "Know what's working",
    subtitle:
      "Track page views, conversions, and user behavior with Google Analytics 4.",
    gradient: "from-blue-400/30 via-indigo-300/20 to-violet-200/30",
  },
  {
    integrationType: "Meta",
    headline: "Maximize your ad spend",
    subtitle:
      "Fire the Meta Pixel on every page to build retargeting audiences and track ROAS.",
    gradient: "from-sky-400/30 via-blue-300/20 to-cyan-200/30",
  },
  {
    integrationType: "TikTok",
    headline: "Reach the next generation",
    subtitle:
      "Add TikTok Pixel tracking to measure ad performance and optimize campaigns.",
    gradient: "from-pink-400/30 via-rose-300/20 to-orange-200/30",
  },
  {
    integrationType: "GoogleTagManager",
    headline: "One tag to rule them all",
    subtitle:
      "Manage all your marketing and analytics tags in one place without touching code.",
    gradient: "from-amber-400/30 via-yellow-300/20 to-orange-200/30",
  },
  {
    integrationType: "Hotjar",
    headline: "See what users actually do",
    subtitle:
      "Understand user behavior with heatmaps, session recordings, and feedback tools.",
    gradient: "from-red-400/30 via-orange-300/20 to-amber-200/30",
  },
  {
    integrationType: "Pinterest",
    headline: "Turn pins into purchases",
    subtitle:
      "Track Pinterest ad conversions and build custom audiences from your store traffic.",
    gradient: "from-rose-400/30 via-red-300/20 to-pink-200/30",
  },
  {
    integrationType: "Snapchat",
    headline: "Capture the moment",
    subtitle:
      "Track conversions and build audiences for your Snapchat advertising campaigns.",
    gradient: "from-yellow-400/30 via-amber-300/20 to-lime-200/30",
  },
];

export const INTEGRATION_CATEGORIES: IntegrationCategory[] = [
  {
    id: "marketing",
    label: "Marketing & Ads",
    description: "Acquire customers and track ad performance.",
    keywords: [
      "marketing",
      "ads",
      "advertising",
      "social",
      "email",
      "sms",
      "campaigns",
      "retargeting",
      "audiences",
      "pixel",
    ],
    integrations: [
      "Meta",
      "TikTok",
      "Snapchat",
      "Pinterest",
      "Reddit",
      "Klaviyo",
    ],
  },
  {
    id: "analytics",
    label: "Analytics & Insights",
    description: "Understand how your store is performing.",
    keywords: [
      "analytics",
      "tracking",
      "insights",
      "data",
      "behavior",
      "heatmaps",
      "recordings",
      "performance",
      "attribution",
      "tags",
    ],
    integrations: [
      "GA4",
      "GoogleTagManager",
      "Hotjar",
      "MicrosoftClarity",
      "Triplewhale",
    ],
  },
];

/**
 * Returns the category for a given integration type, or null if not categorized.
 */
export function getCategoryForIntegration({
  type,
}: {
  type: IntegrationType;
}): IntegrationCategory | null {
  return (
    INTEGRATION_CATEGORIES.find((cat) => cat.integrations.includes(type)) ??
    null
  );
}

/**
 * All integration types that are available across all categories.
 */
export const ALL_CATEGORIZED_INTEGRATIONS: IntegrationType[] =
  INTEGRATION_CATEGORIES.flatMap((cat) => cat.integrations);
